import React, { forwardRef, Fragment } from "react";
import { useIntl } from "react-intl";
import { Icon, Popup, Table, Checkbox } from "semantic-ui-react";
import { useMemo, useCallback } from "react";

import { Config } from "../../../config/api";
import { formatMoney, formatNumber, getApiDate } from "../../../libs/common_utils";
import StrategyGridContext from "../index/context";
import PacingRatioPopup from "./PacingRatioPopup";
import { formatCurrencyByRoundedRules } from "../../campaign/common/CampaignUtils";

/**
 * Generate grid item
 * @param {object} item
 * @param {function} item.cellVisible
 * @param {string} item.currency_code
 * @param {number} item.status
 * @param {string} item.title
 * @param {Date} item.start_date
 * @param {Date} item.end_date
 * @param {number} item.strategy_budget
 * @param {number} item.spend_to_date
 * @param {number} item.cpm
 * @param {number} item.ctr
 * @param {number} item.clicks
 * @param {number} item.impressions
 * @param {number} item.id
 * @return {*}
 * @constructor
 */
const StrategyGridItem = ({item, cellVisible, selectedGridData}) => {
	const intl = useIntl();
	const context = React.useContext(StrategyGridContext);
	const currency = context.currency;
	const nullishCurrencyFormat = value => {
		if (!value && value !== 0) return "-";
		return formatCurrencyByRoundedRules(value, item.currency_code, Config.defaultLocale, 2);
	}
	const { setStrategyForDuplication, setSelectedStrategies, selectedStrategies  } = context;
	const moneyMetrics = useMemo(() => ["offline_revenue", "online_revenue", "spend_to_date", "strategy_max_bid", "strategy_min_bid", "total_revenue", 'total_spend_cpa', 'total_spend_cpc', 'total_spend_cpm', 'total_spend_yesterday'], []);
	const dateMetrics = useMemo(() => ['start_date', 'end_date'], []);
	const numericMetrics = useMemo(() => ['clicks', 'cpm', 'ctr', 'total_conversions', 'offline_conversions', 'online_conversions', 'offline_roi', 'online_roi', 'total_spend_roi', 'video_complete', 'strategy_daily_pacing_spend_to_pace', 'strategy_pacing_ratio'], []);
	const wholeNumberMetrics = useMemo(() => ['impressions'], []);


  const handleCheckboxChange = useCallback((e, { checked }) => {
    if (checked) {
      setSelectedStrategies(prevStrategies => [...prevStrategies, item.id]);
    } else {
      setSelectedStrategies(prevStrategies => prevStrategies.filter(id => id !== item.id));
    }
  }, [item.id]);

  const isStrategyChecked = selectedStrategies.includes(item.id);

	const renderStrategyName = (data) => (
		<Table.Cell className="grid-item-col-name text-ellipsis campaign-name-cell sticky-title" key="campaign_name" data-testid="strategy-grid-row-name">
			<Popup
				size="tiny"
				content={item.title}
				inverted
				trigger={<span>{item.title}</span>}
			/>
		</Table.Cell>
	)

	const renderTableCell = (columnKey, value) => {
		if (!value && value !== 0 && typeof value !== 'boolean') return "-";
		let returnValue = value;
		if (typeof value === "boolean" && columnKey === "status") {
			returnValue = value ? intl.formatMessage({
								id: "STATUS_ACTIVE",
								defaultMessage: "Active",
						  })
						: intl.formatMessage({
								id: "STATUS_INACTIVE",
								defaultMessage: "Inactive",
						  });
		} else if (moneyMetrics.includes(columnKey)) {
			if (columnKey === "strategy_min_bid" && value === 0) return "N/A"
			if (columnKey === "strategy_max_bid" && value >= 9999999.99) return "N/A"
			returnValue = formatCurrencyByRoundedRules(value, item.currency_code, Config.defaultLocale, 2);
		} else if (dateMetrics.includes(columnKey)) {
			returnValue = getApiDate(value, Config.dateTimeFormat);
		} else if (numericMetrics.includes(columnKey)) {
			returnValue = formatNumber(value, Config.defaultLocale);
		} else if (wholeNumberMetrics.includes(columnKey)) {
			returnValue = formatNumber(Math.floor(value), Config.defaultLocale);
		} else if (columnKey === 'strategy_pacing_type') {
			returnValue = value.toUpperCase();
		}
		return returnValue;
	}

	return (
		<Table.Row className={item.status ? "" : " inactive"}>
			<Table.Cell>
				<Checkbox
					name={item.title}
					checked={isStrategyChecked}
					onChange={handleCheckboxChange}
				/>
			</Table.Cell>
			{Object.keys(selectedGridData).map(column => {
				if (!selectedGridData[column]) return <Fragment key={column}></Fragment>;
				if (column === "title") return renderStrategyName();
				if (column === "strategy_pacing_ratio") return <PacingRatioPopup data={item} nullishCurrencyFormat={nullishCurrencyFormat} key={column} />
				return (
					<Table.Cell className={cellVisible(column)} key={column}>
						{renderTableCell(column, item[column])}
					</Table.Cell>
				);
			})}
			<Table.Cell data-clickable="0" textAlign="center">
				<a
					href={null}
					onClick={(e) => {
						e.preventDefault();
						e.stopPropagation();
						context.openChartAccordion();
						context.setStrategySelected(item);
					}}
				>
					<Icon name="line graph" className="control" />
				</a>
				&nbsp;
				<a
					href={null}
					onClick={(e) => {
						e.preventDefault();
						e.stopPropagation();
						setStrategyForDuplication(item);
					}}
				>
					<Icon name="copy outline" className="control" />
				</a>
				&nbsp;
				<a
					href={context.getEditPageHref(item.id)}
					onClick={(e) => {
						if (isNewTabClick(e)) return;
						e.preventDefault();
						e.stopPropagation();
						context.navigateToEditPage(item.id);
					}}
				>
					<Icon name="edit" className="control" />
				</a>
			</Table.Cell>
		</Table.Row>
	);
};

export default StrategyGridItem;
